import '../../styles/react-multi-toggle.css';

import React, { useMemo } from 'react';
import { Box } from '@chakra-ui/layout';
import { ChakraProps } from '@chakra-ui/system';
import { Control, Controller, FieldValues, Path, PathValue } from 'react-hook-form';

import { SearchableDropdown } from './SearchableDropdown';

interface SelectProps<T extends FieldValues, Option extends Record<string | number, unknown>> {
  options: Option[] | undefined;
  valueLabelOptions?: [keyof Option, keyof Option];
  style?: React.CSSProperties & ChakraProps;
  fieldName: Path<T>;
  control: Control<T>;
  placeholder?: string;
  defaultValue: PathValue<T, Path<T>> | undefined;
  isDisabled?: boolean;
  onDependentFieldAndStateUpdate?: () => void;
  isInvalid: boolean;
  isLoading?: boolean;
}

export const SearchableDropdownFormControl = <T extends FieldValues, O extends Record<string | number, unknown>>({
  options,
  valueLabelOptions,
  isDisabled,
  fieldName,
  control,
  placeholder,
  defaultValue,
  onDependentFieldAndStateUpdate,
  isInvalid,
  isLoading,
  ...style
}: SelectProps<T, O>) => {
  /* Transform select options to value-label format */
  const selectOptions = useMemo(() => {
    // Provide a default only if valueLabelOptions is not passed
    const _valueLabelOptions = valueLabelOptions ?? (['value', 'label'] as [keyof O, keyof O]);

    return (
      (options?.length &&
        [...options].map((obj) => ({
          value: obj?.[_valueLabelOptions[0]] as string | number,
          label: obj?.[_valueLabelOptions[1]] as string | number,
        }))) ||
      undefined
    );
  }, [options, valueLabelOptions]);

  return (
    <Controller
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      render={({ field: { value, onChange } }) => {
        return (
          <Box {...style}>
            <SearchableDropdown
              isInvalid={isInvalid}
              isLoading={isLoading}
              placeholder={placeholder}
              options={selectOptions}
              value={value}
              onChange={(newValue) => {
                onChange(newValue);
                //Change other dependencies if any
                //e.g: when manufacturer is selected, clear model value
                onDependentFieldAndStateUpdate && onDependentFieldAndStateUpdate();
              }}
              isDisabled={isDisabled}
            />
          </Box>
        );
      }}
    />
  );
};
